import React, { useEffect, useState } from "react";
import { MODAL_Z } from "../../Components/CommonComponents/ModalPortal";
import ReactDOM from "react-dom";
import axios from "axios";
import { END_POINT, getJwtToken } from "../../Redux/AdminReducer/action";

const ROLE_LABELS = {
  Doctor: "Doctor",
  AssistantDoctor: "Assistant Doctor",
  Receptionist: "Receptionist",
  Consultant: "Consultant",
  Staff: "Staff",
};

const AttendanceModal = ({ date, onClose }) => {
  const [staff, setStaff] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [attendanceTimes, setAttendanceTimes] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${END_POINT}/attendance/members`, {
        headers: {
          Authorization: getJwtToken(),
        },
      });
      setStaff(res.data || []);
      const initialAtt = {};
      const initialTimes = {};
      (res.data || []).forEach((s) => {
        initialAtt[s._id] = null;
        initialTimes[s._id] = { checkIn: "09:00", checkOut: "18:00" };
      });
      setAttendance(initialAtt);
      setAttendanceTimes(initialTimes);
    } catch (error) {
      console.error("Error fetching staff for attendance modal:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (staffId, value) => {
    setAttendance((prev) => ({
      ...prev,
      [staffId]: prev[staffId] === value ? null : value,
    }));
  };

  const handleTimeChange = (staffId, field, timeValue) => {
    setAttendanceTimes((prev) => ({
      ...prev,
      [staffId]: {
        ...(prev[staffId] || { checkIn: "09:00", checkOut: "18:00" }),
        [field]: timeValue,
      },
    }));
  };

  const submitAttendance = async () => {
    try {
      const records = Object.entries(attendance)
        .filter(([_, status]) => status !== null)
        .map(([staffId, status]) => {
          const s = staff?.find((item) => item._id === staffId);
          const times = attendanceTimes[staffId] || { checkIn: "09:00", checkOut: "18:00" };

          let checkInISO = date ? new Date(date).toISOString() : new Date().toISOString();
          let checkOutISO = null;

          if (date && times.checkIn) {
            const [h, m] = times.checkIn.split(":");
            const d = new Date(date);
            d.setHours(parseInt(h, 10) || 9, parseInt(m, 10) || 0, 0, 0);
            checkInISO = d.toISOString();
          }

          if (date && times.checkOut && status === "present") {
            const [h, m] = times.checkOut.split(":");
            const d = new Date(date);
            d.setHours(parseInt(h, 10) || 18, parseInt(m, 10) || 0, 0, 0);
            checkOutISO = d.toISOString();
          }

          return {
            staffId,
            memberRole: s?.role || 'Staff',
            status,
            name: s?.name || s?.role || 'Staff',
            date: date ? new Date(date).toISOString() : new Date().toISOString(),
            checkIn: checkInISO,
            checkOut: checkOutISO,
          };
        });

      if (records.length === 0) {
        alert("Please mark attendance for at least one member first");
        return;
      }

      await Promise.all(
        records.map((record) =>
          axios.post(`${END_POINT}/attendancesubmit`, record, {
            headers: {
              Authorization: getJwtToken(),
            },
          })
        )
      );

      alert("Attendance submitted successfully");
      onClose();
    } catch (error) {
      console.error("Error submitting attendance:", error);
      alert("Failed to submit attendance. Please try again.");
    }
  };

  return ReactDOM.createPortal(
    <div className={`fixed inset-0 ${MODAL_Z} flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4`}>
      <div role="dialog" aria-modal="true" className="relative max-h-[90vh] w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-800">
            Attendance – {date ? new Date(date).toDateString() : ''}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 font-bold text-xl"
          >
            &times;
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          {loading ? (
            <div className="text-center py-6 text-gray-500 text-sm">Loading members...</div>
          ) : !staff || staff.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <p className="font-medium text-gray-700 mb-1">No members found.</p>
              <p className="text-xs text-gray-400">
                Please add doctors, receptionists, consultants or staff in{' '}
                <a href="/staffprofile" className="text-blue-600 underline font-medium">
                  Staff Profile
                </a>{' '}
                first.
              </p>
            </div>
          ) : (
            staff.map((s) => (
              <div
                key={s._id}
                className="p-3 border rounded-lg bg-gray-50 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-800">{s.name}</span>
                    <span className="text-xs text-gray-500">{ROLE_LABELS[s.role] || s.role}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleChange(s._id, "present")}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition ${
                        attendance[s._id] === "present"
                          ? "bg-green-600 text-white border-green-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-green-50"
                      }`}
                    >
                      Present
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange(s._id, "absent")}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition ${
                        attendance[s._id] === "absent"
                          ? "bg-red-600 text-white border-red-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-red-50"
                      }`}
                    >
                      Absent
                    </button>
                  </div>
                </div>

                {attendance[s._id] === "present" && (
                  <div className="flex items-center gap-4 bg-white p-2.5 rounded border border-gray-200 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-600">Punch In:</span>
                      <input
                        type="time"
                        value={attendanceTimes[s._id]?.checkIn || "09:00"}
                        onChange={(e) => handleTimeChange(s._id, "checkIn", e.target.value)}
                        className="border rounded px-2 py-1 bg-gray-50 focus:bg-white focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-600">Punch Out:</span>
                      <input
                        type="time"
                        value={attendanceTimes[s._id]?.checkOut || "18:00"}
                        onChange={(e) => handleTimeChange(s._id, "checkOut", e.target.value)}
                        className="border rounded px-2 py-1 bg-gray-50 focus:bg-white focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 rounded-md border border-gray-300 bg-white"
          >
            Cancel
          </button>
          <button
            onClick={submitAttendance}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm"
          >
            Submit Attendance
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AttendanceModal;